/**
 * หน้าเข้าสู่ระบบและหน้าตั้งรหัสผ่านใหม่
 *
 * ออกแบบให้ใช้กลางแดดได้ ช่องกรอกใหญ่ ปุ่มใหญ่ และมีปุ่มดูรหัสที่พิมพ์
 * เพราะเจ้าหน้าที่พิมพ์บนมือถือกลางแจ้งซึ่งพิมพ์ผิดง่าย
 */
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { checkPassword } from '../../shared/password';

interface LoginProps {
  onSignedIn: () => void;
}

export function LoginRoute({ onSignedIn }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.login(email.trim(), password);
      onSignedIn();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'เข้าสู่ระบบไม่สำเร็จ');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wrap stack-lg" style={{ paddingTop: '10vh', maxWidth: '26rem' }}>
      <div className="card stack">
        <h1 className="card-title">เข้าสู่ระบบ</h1>
        <p className="card-sub">MeterPoint · บันทึกจุดติดตั้ง</p>

        {error && (
          <div className="banner banner-bad" role="alert">
            <span>{error}</span>
          </div>
        )}

        <form className="stack" onSubmit={(event) => void submit(event)}>
          <div className="field">
            <label className="label" htmlFor="login-email">
              ชื่อผู้ใช้
            </label>
            <input
              id="login-email"
              className="input"
              type="text"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              required
              disabled={busy}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="login-password">
              รหัสผ่าน
            </label>
            <input
              id="login-password"
              className="input"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
            />
            <label className="row small">
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(event) => setShowPassword(event.target.checked)}
              />
              <span>แสดงรหัสผ่าน</span>
            </label>
          </div>

          <button type="submit" className="btn btn-block" disabled={busy || !email || !password}>
            {busy ? 'กำลังเข้าสู่ระบบ…' : 'เข้าสู่ระบบ'}
          </button>
        </form>

        <p className="small muted">
          ลืมรหัสผ่าน กรุณาติดต่อผู้ดูแลระบบเพื่อขอรหัสชั่วคราว ระบบไม่มีการส่งอีเมลรีเซ็ตรหัสผ่าน
        </p>
      </div>
    </div>
  );
}

interface ChangePasswordProps {
  onChanged: () => void;
  minPasswordLength: number;
  /** true = ถูกบังคับให้เปลี่ยนเพราะยังใช้รหัสชั่วคราวอยู่ */
  forced: boolean;
  email: string;
  displayName: string;
}

export function ChangePasswordRoute({
  onChanged,
  minPasswordLength,
  forced,
  email,
  displayName,
}: ChangePasswordProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ตรวจฝั่ง client เพื่อบอกผู้ใช้ทันที server ตรวจซ้ำด้วยกฎเดียวกันเสมอ
  const check = checkPassword(
    newPassword,
    { minLength: minPasswordLength },
    { email, displayName },
  );
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit =
    !busy && currentPassword.length > 0 && check.valid && !mismatch && confirmPassword.length > 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(currentPassword, newPassword);
      onChanged();
    } catch (caught) {
      if (caught instanceof ApiError) {
        const problems = (caught.details as { problems?: string[] } | undefined)?.problems;
        setError(problems?.length ? problems.join(' · ') : caught.message);
      } else {
        setError('เปลี่ยนรหัสผ่านไม่สำเร็จ');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wrap stack-lg" style={{ paddingTop: '8vh', maxWidth: '26rem' }}>
      <div className="card stack">
        <h1 className="card-title">{forced ? 'ตั้งรหัสผ่านใหม่' : 'เปลี่ยนรหัสผ่าน'}</h1>

        {forced && (
          <div className="banner banner-warn">
            <span>
              คุณกำลังใช้รหัสชั่วคราวที่ผู้ดูแลระบบตั้งให้ กรุณาตั้งรหัสของตัวเองก่อนเริ่มใช้งาน
            </span>
          </div>
        )}

        {error && (
          <div className="banner banner-bad" role="alert">
            <span>{error}</span>
          </div>
        )}

        <form className="stack" onSubmit={(event) => void submit(event)}>
          <div className="field">
            <label className="label" htmlFor="current-password">
              {forced ? 'รหัสชั่วคราว' : 'รหัสผ่านปัจจุบัน'}
            </label>
            <input
              id="current-password"
              className="input"
              type={showPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="new-password">
              รหัสผ่านใหม่
            </label>
            <input
              id="new-password"
              className="input"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              aria-invalid={newPassword.length > 0 && !check.valid}
              required
              disabled={busy}
            />
            {newPassword.length > 0 && !check.valid ? (
              <ul
                className="small"
                style={{ color: 'var(--bad)', margin: 0, paddingInlineStart: '1.2em' }}
              >
                {check.problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            ) : (
              <span className="small muted">ยาวอย่างน้อย {minPasswordLength} ตัวอักษร</span>
            )}
          </div>

          <div className="field">
            <label className="label" htmlFor="confirm-password">
              ยืนยันรหัสผ่านใหม่
            </label>
            <input
              id="confirm-password"
              className="input"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              aria-invalid={mismatch}
              required
              disabled={busy}
            />
            {mismatch && (
              <span className="small" style={{ color: 'var(--bad)' }}>
                รหัสผ่านสองช่องไม่ตรงกัน
              </span>
            )}
          </div>

          <label className="row small">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(event) => setShowPassword(event.target.checked)}
            />
            <span>แสดงรหัสผ่าน</span>
          </label>

          <button type="submit" className="btn btn-block" disabled={!canSubmit}>
            {busy ? 'กำลังบันทึก…' : 'บันทึกรหัสผ่านใหม่'}
          </button>
        </form>

        <p className="small muted">
          เมื่อเปลี่ยนรหัสแล้ว เครื่องอื่นที่ล็อกอินค้างอยู่จะถูกตัดออกจากระบบทั้งหมด
        </p>
      </div>
    </div>
  );
}
